// https://github.com/markdown-it/markdown-it
import MarkdownIt from "markdown-it"
import { useEffect } from "react"
import { useState } from "react"

/*

================================================================================
------------------------------------- STOP -------------------------------------
Changes to this file will result in changes to how markdown renders.

They'll require:
- complex data migrations to ensure existing data still renders the same
- parallel changes to vd-shared and mobi to stay in sync

No changes should be made without an explicit feature request and the above
steps also addressed.
------------------------------------- STOP -------------------------------------
================================================================================

*/

// MarkdownIt doesn't provide allowlist functionality
// so we need to do some introspection in order to create it
// NOTE: There's also a `core` category but turning anything
// off in it is fairly detrimental
const categories = ["block", "inline"]
export const allRules = []
export const rulesByCategory = {}
;(function () {
  const md = new MarkdownIt()
  const forced = [
    "balance_pairs", // most rules don't work without this
    "paragraph", // infinite loop without it
  ]
  categories.forEach((category) => {
    // @ts-ignore PT - #180597146
    rulesByCategory[category] = []
    // @ts-ignore PT - #180597146
    Object.keys(md[category]).forEach((rulerKey) => {
      // @ts-ignore PT - #180597146
      const internalRules = md[category][rulerKey].__rules__
      internalRules.forEach((rule: any) => {
        if (forced.includes(rule.name)) {
          return
        }
        // @ts-ignore
        if (!allRules.includes(rule.name)) {
          // @ts-ignore PT - #180597146
          rulesByCategory[category].push(rule.name)
          // @ts-ignore
          allRules.push(rule.name)
        }
      })
    })
  })
})()

function create() {
  const md = new MarkdownIt({
    html: true,
    linkify: true,
    breaks: true,
  })

  // Set markdown links to always open in a new tab
  md.renderer.rules.link_open = function (
    tokens: any[],
    index: number,
    _options: any,
    _env: any,
    renderer: any,
  ) {
    const token = tokens[index]
    token.attrPush(["target", "_blank"])
    return renderer.renderToken(...Array.from(arguments))
  }

  return md
}

interface UseMarkdownOptions {
  allow?: string[]
  inline?: boolean
}

/**
  Returns html rendered from markdown.

  @example
  useMarkdown('*Markdown text*'); => '<em>Markdown text</em>'

  @example
  useMarkdown('...', { allow: ['inline'] });

*/
export default function useMarkdown(text: string, options: UseMarkdownOptions = {}) {
  // NOTE: If text is null/undefined, the markdown functions later on
  // blow up trying to do text.replace() on it, causing the whole page
  // to show an error. This fallback lets the page still render.
  // In the future the TS null checks will likely prevent it too,
  // but blowing up is never good so can still leave the fallback.
  // - MD
  text = text || ""
  const [html, setHtml] = useState("")
  useEffect(() => {
    const md = create()

    if (options.allow) {
      md.options.html = false
      md.options.linkify = false
      md.options.breaks = true
      md.disable(allRules)

      // Enabled the newline rule to support line breaks
      md.enable("newline")

      options.allow.forEach((rule) => {
        if (rule.includes("html")) {
          md.options.html = true
        }
        if (rule === "autolink") {
          // Seems like autolink does nothing and linkify does it all
          // but they seem to serve the same purpose as autolink has code
          // to detect urls and emails and link them
          md.options.linkify = true
        }
        if (categories.includes(rule)) {
          // @ts-ignore PT - #180597146
          md.enable(rulesByCategory[rule])
        } else {
          md.enable(rule)
        }
      })
    }

    if (options.inline) {
      setHtml(md.renderInline(text))
    } else {
      setHtml(md.render(text))
    }
  }, [text, options.allow?.join(" "), options.inline])
  return html
}
